
import React from 'react';
import { Load, EfficiencyRecommendation } from './types';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Lightbulb, Sparkle, Wand } from 'lucide-react';

interface EfficiencyRecommendationsProps {
  loads: Load[];
  activeRecommendation: string | null;
  toggleRecommendation: (recId: string) => void;
  implementRecommendation: (loadId: string, recId: string) => void;
}

const EfficiencyRecommendations: React.FC<EfficiencyRecommendationsProps> = ({
  loads,
  activeRecommendation,
  toggleRecommendation,
  implementRecommendation
}) => {
  const getRecommendationIcon = (priority: 'high' | 'medium' | 'low') => {
    switch (priority) {
      case 'high':
        return <Sparkle className="h-4 w-4 text-amber-500" />;
      case 'medium':
        return <Wand className="h-4 w-4 text-blue-500" />;
      case 'low':
        return <Lightbulb className="h-4 w-4 text-green-500" />;
    }
  };

  return (
    <div className="mb-6">
      <h3 className="text-lg font-semibold mb-4 text-gray-700 dark:text-gray-300">
        <Sparkle className="inline-block mr-2 h-5 w-5 text-amber-500" />
        Efficiency Recommendations
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {loads.flatMap(load => 
          load.recommendations.map(rec => (
            <Card 
              key={rec.id}
              className={`shadow-sm border transition-all ${
                activeRecommendation === rec.id
                  ? 'border-blue-300 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800'
                  : 'border-gray-200 dark:border-gray-800'
              }`}
            >
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center">
                    {getRecommendationIcon(rec.priority)}
                    <span className="ml-2 font-medium text-gray-800 dark:text-white">
                      {load.name}
                    </span>
                  </div>
                  <div className={`text-xs px-2 py-1 rounded-full ${
                    rec.priority === 'high' 
                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                      : rec.priority === 'medium'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                        : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                  }`}>
                    {rec.priority.charAt(0).toUpperCase() + rec.priority.slice(1)} Priority
                  </div>
                </div>
                
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  {rec.description}
                </p>
                
                <div className="flex justify-between items-center mt-3">
                  <div className="text-sm">
                    <span className="text-green-600 dark:text-green-400 font-medium">
                      Potential Savings: {rec.potentialSavings}
                    </span>
                  </div>
                  
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleRecommendation(rec.id)}
                      className="text-xs h-8"
                    >
                      {activeRecommendation === rec.id ? 'Hide Details' : 'View Details'}
                    </Button>
                    
                    {activeRecommendation === rec.id && (
                      <Button
                        size="sm"
                        onClick={() => implementRecommendation(load.id, rec.id)}
                        className="text-xs h-8 bg-green-600 hover:bg-green-700 text-white"
                      >
                        Implement
                      </Button>
                    )}
                  </div>
                </div>
                
                {activeRecommendation === rec.id && (
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-400">
                    <p>Implementing this recommendation will optimize energy usage for this load and reduce operational costs. The estimated savings are calculated based on current usage patterns and industry benchmarks.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default EfficiencyRecommendations;
